// Pure derivations over the `GET /api/organizer/clubs/:clubId/stats` response.
//
// The back does the accounting (see its README, "Estadísticas de canchas"); this module only
// reshapes that response for display: flat metrics, chart series, the weekday x hour grid, insights,
// the per-court table and the filter options. Ratios with an empty denominator stay `null` all the
// way down so the UI shows a dash instead of a misleading 0.

import { SPORTS, SPORT_LABELS } from "../../constants/courtDomain";
import { daysInRange } from "./statsDates";
import { buildHeatGrid as sharedBuildHeatGrid, heatExtremes, heatHours as sharedHeatHours, weekdayLong, weekdayPlural } from "./statsHeat";
import { fmtMoney, fmtPct0, hourRange } from "./statsFormat";

const minutesToHours = (m) => (typeof m === "number" ? m / 60 : null);

/**
 * Flat metrics of one response. `perDay` averages the reservations over the days of the range.
 * @returns {object|null} null when there is no response yet.
 */
export const deriveMetrics = (info) => {
  const t = info?.totals;
  if (!t) return null;
  const days = daysInRange({ from: info.from, to: info.to });
  return {
    occupancy: t.occupancy,
    revenue: t.revenueCollected?.total ?? null,
    revenueOnline: t.revenueCollected?.online ?? null,
    revenuePos: t.revenueCollected?.pos ?? null,
    reservations: t.reservations,
    perDay: t.reservations / days,
    revenuePerHour: t.revenuePerAvailableHour,
    cancellationRate: t.cancellationRate,
    noShowRate: t.noShowRate,
    cancelled: t.cancelled,
    noShows: t.noShows,
    pending: t.pending,
    noShowUncollected: t.noShowUncollected,
    unsoldHours: t.unsoldHours,
    availableHours: minutesToHours(t.availableMinutes),
    bookedHours: minutesToHours(t.bookedMinutes),
    avgLeadHours: t.avgLeadHours,
    sameDayShare: t.sameDayShare,
  };
};

/**
 * Chart series from the zero-filled `daily` array, grouped in buckets of `bucket` days.
 * - occ: booked / available minutes of the bucket (null when nothing was available).
 * - iph: revenue per available hour (null when nothing was available).
 * - rev / res: daily average inside the bucket, so a short last bucket is not understated.
 *
 * @param {Array} daily
 * @param {"occ"|"iph"|"rev"|"res"} metric
 * @param {number} bucket  1 (daily) or 7 (weekly)
 * @returns {Array<{index: number, date: string, value: number|null}>}  `date` is the bucket's first day.
 */
export const buildSeries = (daily, metric, bucket) => {
  const points = [];
  for (let i = 0; i < daily.length; i += bucket) {
    const slice = daily.slice(i, i + bucket);
    const sum = (key) => slice.reduce((acc, d) => acc + d[key], 0);
    const available = sum("availableMinutes");
    let value;
    if (metric === "occ") value = available ? sum("bookedMinutes") / available : null;
    else if (metric === "iph") value = available ? sum("revenue") / (available / 60) : null;
    else if (metric === "rev") value = sum("revenue") / slice.length;
    else value = sum("reservations") / slice.length;
    points.push({ index: points.length, date: slice[0].date, value });
  }
  return points;
};

export { sparkValues } from "./statsChart"; // shared with Beauty

/* ------------------------------ weekday x hour heat ------------------------------ */

// The grid logic is shared with Beauty (statsHeat.js); courts open minutes live in `availableMinutes`.
export const heatHours = (heat) => sharedHeatHours(heat, "availableMinutes");
export const buildHeatGrid = (info) => sharedBuildHeatGrid(info, "availableMinutes");
export { weekdayLong, weekdayPlural };

/* ------------------------------------ insights ------------------------------------ */

const sportLabel = (sport) => SPORT_LABELS[sport] || sport;

/**
 * Up to three cards under the KPI tiles. `parts` alternate plain text and bold text (odd indexes
 * are bold), so the component can render them without HTML strings.
 *
 * 1. Peak slot, 2. quietest slot (and how many slots sit under 25 %), 3. the court that earns the
 * most per available hour (needs 2+ courts) or, with a single court, the money lost to no-shows.
 * Nothing is shown when the period has no bookings: a "peak" of 0 % is not an insight.
 */
export const selectInsights = (info) => {
  const metrics = deriveMetrics(info);
  if (!metrics || !metrics.reservations) return [];

  const { peak, low, lowCount } = heatExtremes(buildHeatGrid(info), 0.25);

  const cards = [];
  if (peak) {
    cards.push({
      key: "peak",
      eyebrow: "Horario pico",
      tone: "peak",
      parts: [
        "Los ",
        `${weekdayPlural(peak.weekday)} de ${hourRange(peak.hour)}`,
        " son tu franja más pedida: ",
        `${fmtPct0(peak.value)} ocupado`,
        ". Ahí conviene sostener el precio de hora pico.",
      ],
    });
  }
  if (low) {
    cards.push({
      key: "valley",
      eyebrow: "Horas valle",
      tone: "valley",
      parts: [
        "Hay ",
        `${lowCount} ${lowCount === 1 ? "franja" : "franjas"} por semana`,
        " por debajo del 25 % de ocupación. La más floja: ",
        `${weekdayPlural(low.weekday)} de ${hourRange(low.hour)} (${fmtPct0(low.value)})`,
        ". Buen lugar para un precio valle, clases o escuelita.",
      ],
    });
  }

  const ranked = info.perCourt
    .filter((c) => c.availableMinutes > 0)
    .map((c) => ({ ...c, perHour: c.revenue / (c.availableMinutes / 60) }))
    .sort((a, b) => b.perHour - a.perHour);
  if (info.perCourt.length >= 2 && ranked.length > 0) {
    const best = ranked[0];
    const average = metrics.revenuePerHour;
    const above = average > 0 ? best.perHour / average - 1 : 0;
    cards.push({
      key: "bestCourt",
      eyebrow: "Cancha que más rinde",
      tone: "ink",
      parts: [
        "",
        `${best.name} (${sportLabel(best.sport)})`,
        " genera ",
        `${fmtMoney(best.perHour)} por hora disponible`,
        above > 0 ? `, ${fmtPct0(above)} más que el promedio del club.` : ".",
      ],
    });
  } else if (info.perCourt.length < 2 && metrics.noShows > 0) {
    cards.push({
      key: "noShow",
      eyebrow: "Ausencias",
      tone: "ink",
      parts: [
        "Las ausencias dejaron ",
        `${fmtMoney(metrics.noShowUncollected)} sin cobrar`,
        ` en ${metrics.noShows} ${metrics.noShows === 1 ? "turno" : "turnos"}. `,
        "Pedir seña en las reservas de mostrador",
        " reduce ese número.",
      ],
    });
  }
  return cards;
};

/* ------------------------------------ per court ------------------------------------ */

/** Table rows from `perCourt`, with the per-hour revenue and the two rates (null when empty). */
export const buildCourtRows = (perCourt) =>
  perCourt.map((c) => ({
    id: c.id,
    name: c.name,
    sport: c.sport,
    enclosure: c.enclosure,
    reservations: c.reservations,
    bookedHours: c.bookedMinutes / 60,
    occupancy: c.occupancy,
    revenue: c.revenue,
    revenuePerHour: c.availableMinutes > 0 ? c.revenue / (c.availableMinutes / 60) : null,
    cancellationRate: c.cancelled + c.reservations > 0 ? c.cancelled / (c.cancelled + c.reservations) : null,
    noShowRate: c.reservations > 0 ? c.noShows / c.reservations : null,
  }));

// Sorting is shared with Beauty (statsSort.js).
export { sortRows as sortCourtRows, nextSort } from "./statsSort";

/* ------------------------------ channels and collections ------------------------------ */

export { channelShares } from "./statsChannels"; // shared with Beauty

/** What was billed in the period: collected online, collected at the counter, still pending. */
export const collectionRows = (totals) => {
  const rows = [
    { key: "online", label: "Cobrado online (seña o total)", amount: totals.revenueCollected.online },
    { key: "pos", label: "Cobrado en mostrador (ZocoPDV)", amount: totals.revenueCollected.pos },
    { key: "pending", label: "Pendiente de cobro", amount: totals.pending },
  ];
  const billed = rows.reduce((acc, r) => acc + r.amount, 0);
  return rows.map((r) => ({ ...r, share: billed ? r.amount / billed : null }));
};

/* ------------------------------------ filters ------------------------------------ */

// A court that was soft-deleted ("dado de baja") has `status: false`. The back answers 400 for its
// id, so it is never offered as a filter even though the courts endpoint may still list it.
const activeCourts = (courts) => courts.filter((c) => c.status !== false);

/** Sports of the club's active courts, in the app's canonical order. */
export const sportFilterOptions = (courts) => {
  const present = new Set(activeCourts(courts).map((c) => c.sport).filter(Boolean));
  const known = SPORTS.filter((s) => present.has(s));
  const unknown = [...present].filter((s) => !SPORTS.includes(s));
  return [...known, ...unknown].map((value) => ({ value, label: sportLabel(value) }));
};

/** Active courts, narrowed by sport when one is selected ("all" keeps every sport). */
export const courtFilterOptions = (courts, sport) =>
  activeCourts(courts)
    .filter((c) => sport === "all" || c.sport === sport)
    .map((c) => ({ value: c._id, label: `${c.name} · ${sportLabel(c.sport)}` }));

/** Keeps the selected court only while it is still an option; otherwise back to "all". */
export const validCourtSelection = (courtId, courts, sport) =>
  courtFilterOptions(courts, sport).some((o) => o.value === courtId) ? courtId : "all";

/** Query for the stats endpoint: the range, plus sport/court only when the filter is not "all". */
export const buildStatsParams = (range, sport, courtId) => ({
  from: range.from,
  to: range.to,
  ...(sport && sport !== "all" ? { sport } : {}),
  ...(courtId && courtId !== "all" ? { courtId } : {}),
});
